import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { browseTagFolder, getFolderTree, searchTags } from "../api";
import chevronIcon from "../assets/chevron-right.svg";
import folderIcon from "../assets/folder.svg";
import searchIconSrc from "../assets/search.svg";
import tagIcon from "../assets/page-flip.svg";
import { compareFolderRows } from "./tagOrder";
import "../styles/gateway/tag-picker.css";

// This follows the editor TagTreePicker's scalable contract: the initial call
// returns folders + counts only, folder leaves load in bounded pages, and
// search runs on the server. A 100k-tag folder therefore costs the browser the
// same as a ten-tag folder until the operator opens or searches it.
const ROOT_FOLDER_ID = "__root__";
const FOLDER_PAGE_SIZE = 250;
const SEARCH_PAGE_SIZE = 250;
const SEARCH_DEBOUNCE_MS = 150;
const SOURCE_LABELS = {
  manual: "Manual",
  plc: "PLC",
  edge_gpio: "GPIO",
  ncd_sensor: "Control Seat sensor",
  mqtt: "MQTT",
  opcua: "OPC UA",
  modbus: "Modbus",
  sql: "SQL",
  influxdb: "InfluxDB",
  prometheus: "Prometheus",
  http: "HTTP",
  pi: "PI System",
  sim: "Simulation",
  computed: "Computed",
};

const folderID = (folder) => String(folder?.id || "");
const tagID = (tag) => tag?.tagId ?? tag?.id ?? "";
const tagPath = (tag) => String(tag?.fullPath || tag?.full_path || tag?.path || "");
const tagKey = (tag, index) => String(tagID(tag) || tagPath(tag) || `tag-${index}`);
const sourceLabel = (tag) => {
  const kind = String(tag?.source_kind || tag?.source?.kind || "").trim().toLowerCase();
  return kind ? SOURCE_LABELS[kind] || kind.replace(/[_-]+/g, " ") : "";
};

const mergeFolderPage = (folders, targetID, page, append) => folders.map((folder) => {
  if (folderID(folder) !== targetID) return folder;
  const incoming = Array.isArray(page?.tags) ? page.tags : [];
  const current = append && Array.isArray(folder.tags) ? folder.tags : [];
  const tags = current.slice();
  const positions = new Map(tags.map((tag, index) => [tagKey(tag, index), index]));
  incoming.forEach((tag, index) => {
    const key = tagKey(tag, index);
    if (positions.has(key)) tags[positions.get(key)] = tag;
    else {
      positions.set(key, tags.length);
      tags.push(tag);
    }
  });
  const total = Number.isFinite(Number(page?.total)) ? Number(page.total) : tags.length;
  return { ...folder, tags, tagCount: total, tagsLoaded: true, tagsLoading: false, tagsError: false };
});

function TagRow({ tag, selected, onSelect, searchResult = false }) {
  const id = tagID(tag);
  const path = tagPath(tag);
  const source = sourceLabel(tag);
  const details = [source, tag?.type, tag?.writable ? "Writable" : ""].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      className={`tpTag${selected ? " isSelected" : ""}`}
      disabled={id === "" || !path}
      onClick={() => onSelect(id, path)}
      title={path}
      aria-pressed={selected}
    >
      <img src={tagIcon} alt="" className="tpIcon gwIcon" />
      <span className="tpTagText">
        <span className="tpName">{tag?.name || tag?.path || path}</span>
        {(searchResult || details) && <span className="tpPath">{searchResult ? path : details}</span>}
      </span>
      {searchResult && details ? <span className="tpMeta">{details}</span> : null}
    </button>
  );
}

function FolderNode({
  folder,
  foldersByParent,
  depth,
  expanded,
  selectedPath,
  onToggle,
  onLoadMore,
  onSelect,
}) {
  const id = folderID(folder);
  const open = expanded.has(id);
  const children = foldersByParent.get(id) || [];
  const tags = Array.isArray(folder.tags) ? folder.tags : [];
  const count = Number(folder.tagCount) || 0;
  const canLoadMore = folder.tagsLoaded && tags.length < count;
  return (
    <div className="tpFolderNode">
      <button
        type="button"
        className="tpFolder"
        onClick={() => onToggle(id)}
        style={{ paddingLeft: 8 + depth * 16 }}
        aria-expanded={open}
      >
        <img src={chevronIcon} alt="" className={`tpChevron gwIcon${open ? " isOpen" : ""}`} />
        <img src={folderIcon} alt="" className="tpIcon gwIcon" />
        <span className="tpName">{folder.name || "Folder"}</span>
        {count > 0 ? <span className="tpCount">{count.toLocaleString()}</span> : null}
      </button>
      {open ? (
        <div>
          {children.map((child) => (
            <FolderNode
              key={folderID(child)}
              folder={child}
              foldersByParent={foldersByParent}
              depth={depth + 1}
              expanded={expanded}
              selectedPath={selectedPath}
              onToggle={onToggle}
              onLoadMore={onLoadMore}
              onSelect={onSelect}
            />
          ))}
          {folder.tagsLoading ? <div className="tpEmpty tpInlineStatus">Loading tags…</div> : null}
          {folder.tagsError ? <div className="tpEmpty tpInlineStatus">Tags could not be loaded. Collapse and try again.</div> : null}
          {!folder.tagsLoading && tags.map((tag, index) => (
            <div key={tagKey(tag, index)} style={{ paddingLeft: 16 + depth * 16 }}>
              <TagRow tag={tag} selected={tagPath(tag) === selectedPath} onSelect={onSelect} />
            </div>
          ))}
          {canLoadMore ? (
            <button type="button" className="tpLoadMore" onClick={() => onLoadMore(id)}>
              Load more · {tags.length.toLocaleString()} of {count.toLocaleString()}
            </button>
          ) : null}
          {!folder.tagsLoading && folder.tagsLoaded && !tags.length && !children.length ? (
            <div className="tpEmpty tpInlineStatus">This folder is empty.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Compact picker backed by the same lazy tag-tree endpoints as the editor.
 * onChange receives (tagId, fullPath); tag ids remain internal and are never
 * rendered in the UI.
 */
export default function TagPicker({
  value,
  onChange,
  placeholder = "Select a tag…",
  displayValue,
  worksheetId,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPage, setSearchPage] = useState({ tags: [], total: 0 });
  const [searchLoading, setSearchLoading] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const dataGeneration = useRef(0);
  const loadingFolders = useRef(new Map());

  const close = useCallback(() => {
    setOpen(false);
    setSearchInput("");
    setSearchQuery("");
  }, []);

  useEffect(() => {
    if (disabled) close();
  }, [close, disabled]);

  useEffect(() => {
    const generation = dataGeneration.current + 1;
    dataGeneration.current = generation;
    loadingFolders.current.clear();
    if (!open) return undefined;
    let cancelled = false;
    setFolders([]);
    setExpanded(new Set());
    setTreeLoading(true);
    setTreeError(false);
    getFolderTree({ worksheetId, lite: true, countsOnly: true })
      .then(async (payload) => {
        if (cancelled || dataGeneration.current !== generation) return;
        const next = Array.isArray(payload?.browsers) ? payload.browsers : [];
        setFolders(next);
        const root = next.find((folder) => folderID(folder) === ROOT_FOLDER_ID);
        if (!root || Number(root.tagCount) <= 0) return;
        try {
          const page = await browseTagFolder(ROOT_FOLDER_ID, { worksheetId, limit: FOLDER_PAGE_SIZE });
          if (!cancelled && dataGeneration.current === generation) {
            setFolders((current) => mergeFolderPage(current, ROOT_FOLDER_ID, page, false));
          }
        } catch {
          if (!cancelled && dataGeneration.current === generation) {
            setFolders((current) => current.map((folder) => (
              folderID(folder) === ROOT_FOLDER_ID ? { ...folder, tagsError: true, tagsLoaded: true } : folder
            )));
          }
        }
      })
      .catch(() => {
        if (!cancelled && dataGeneration.current === generation) setTreeError(true);
      })
      .finally(() => {
        if (!cancelled && dataGeneration.current === generation) setTreeLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, worksheetId]);

  useEffect(() => {
    if (searchInput.trim() === searchQuery.trim()) return undefined;
    const timer = window.setTimeout(() => setSearchQuery(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput, searchQuery]);

  useEffect(() => {
    if (!open || !searchQuery.trim()) {
      setSearchPage({ tags: [], total: 0 });
      setSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSearchLoading(true);
    searchTags({ query: searchQuery, worksheetId, limit: SEARCH_PAGE_SIZE })
      .then((page) => { if (!cancelled) setSearchPage(page || { tags: [], total: 0 }); })
      .catch(() => { if (!cancelled) setSearchPage({ tags: [], total: 0, error: true }); })
      .finally(() => { if (!cancelled) setSearchLoading(false); });
    return () => { cancelled = true; };
  }, [open, searchQuery, worksheetId]);

  const positionMenu = useCallback(() => {
    const trigger = inputRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = Math.max(320, window.innerWidth || 0);
    const width = Math.min(Math.max(rect.width, 360), 560, viewportWidth - 16);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, viewportWidth - width - 8));
    const height = Math.min(menu.offsetHeight || 360, 360);
    const below = window.innerHeight - rect.bottom - 8;
    const top = below >= Math.min(height, 220)
      ? rect.bottom + 4
      : Math.max(8, rect.top - height - 4);
    Object.assign(menu.style, {
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      width: `${Math.round(width)}px`,
    });
  }, []);

  const menuRefCallback = useCallback((node) => {
    menuRef.current = node;
    if (node) window.requestAnimationFrame(positionMenu);
  }, [positionMenu]);

  useEffect(() => {
    if (!open) return undefined;
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    const observer = typeof ResizeObserver === "function" && menuRef.current
      ? new ResizeObserver(positionMenu)
      : null;
    observer?.observe(menuRef.current);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
      observer?.disconnect();
    };
  }, [open, positionMenu]);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event) => {
      if (wrapRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      close();
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [close, open]);

  const foldersByParent = useMemo(() => {
    const result = new Map();
    folders.forEach((folder) => {
      const id = folderID(folder);
      if (!id || id === ROOT_FOLDER_ID) return;
      const parent = folder.parentId || "__top__";
      if (!result.has(parent)) result.set(parent, []);
      result.get(parent).push(folder);
    });
    result.forEach((items) => items.sort((left, right) => {
      return compareFolderRows(left, right);
    }));
    return result;
  }, [folders]);

  // Folder and search endpoints sort before pagination. Preserve that order
  // exactly: re-sorting one returned page in the browser can move rows across
  // page boundaries and make Load more skip or duplicate a tag.
  const orderedSearchResults = Array.isArray(searchPage.tags) ? searchPage.tags : [];

  const loadFolder = useCallback((id, append = false) => {
    const generation = dataGeneration.current;
    if (!id || loadingFolders.current.get(id) === generation) return;
    const folder = folders.find((candidate) => folderID(candidate) === id);
    if (!folder) return;
    const offset = append && Array.isArray(folder.tags) ? folder.tags.length : 0;
    loadingFolders.current.set(id, generation);
    setFolders((current) => current.map((candidate) => (
      folderID(candidate) === id ? { ...candidate, tagsLoading: true, tagsError: false } : candidate
    )));
    browseTagFolder(id, { worksheetId, limit: FOLDER_PAGE_SIZE, offset })
      .then((page) => {
        if (dataGeneration.current === generation) {
          setFolders((current) => mergeFolderPage(current, id, page, append));
        }
      })
      .catch(() => {
        if (dataGeneration.current === generation) {
          setFolders((current) => current.map((candidate) => (
            folderID(candidate) === id ? { ...candidate, tagsLoading: false, tagsError: true } : candidate
          )));
        }
      })
      .finally(() => {
        if (loadingFolders.current.get(id) === generation) loadingFolders.current.delete(id);
      });
  }, [folders, worksheetId]);

  const toggleFolder = useCallback((id) => {
    const opening = !expanded.has(id);
    const folder = folders.find((candidate) => folderID(candidate) === id);
    if (opening && folder && !folder.tagsLoaded && Number(folder.tagCount) > 0) loadFolder(id);
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [expanded, folders, loadFolder]);

  const select = useCallback((id, path) => {
    onChange(String(id), path);
    close();
    window.requestAnimationFrame(() => inputRef.current?.querySelector(".tpInputToggle")?.focus());
  }, [close, onChange]);

  const root = folders.find((folder) => folderID(folder) === ROOT_FOLDER_ID);
  const rootTags = Array.isArray(root?.tags) ? root.tags : [];
  const topFolders = foldersByParent.get("__top__") || [];
  const query = searchQuery.trim();
  const searchPending = searchInput.trim() !== searchQuery.trim();

  const menu = open && !disabled ? createPortal(
    <div ref={menuRefCallback} className="tpMenu" role="dialog" aria-label="Choose a tag">
      <div className="tpSearchWrap">
        <div className="gwSearch tpSearch">
          <img src={searchIconSrc} alt="" className="gwSearchIcon" />
          <input
            placeholder="Search tags…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Escape") return;
              close();
              window.requestAnimationFrame(() => inputRef.current?.querySelector(".tpInputToggle")?.focus());
            }}
            autoFocus
          />
          {searchPending ? <span className="tpSearching">Searching…</span> : null}
        </div>
      </div>
      <div className="tpList">
        {query ? (
          <>
            {searchLoading ? <div className="tpEmpty">Searching…</div> : null}
            {!searchLoading && searchPage.error ? <div className="tpEmpty">Search is unavailable. Try again.</div> : null}
            {!searchLoading && !searchPage.error && !orderedSearchResults.length ? <div className="tpEmpty">No tags found.</div> : null}
            {!searchLoading && orderedSearchResults.map((tag, index) => (
              <TagRow key={tagKey(tag, index)} tag={tag} selected={tagPath(tag) === displayValue} onSelect={select} searchResult />
            ))}
            {!searchLoading && Number(searchPage.total) > orderedSearchResults.length ? (
              <div className="tpResultSummary">
                Showing {orderedSearchResults.length.toLocaleString()} of {Number(searchPage.total).toLocaleString()} matches. Refine your search.
              </div>
            ) : null}
          </>
        ) : (
          <>
            {treeLoading ? <div className="tpEmpty">Loading tags…</div> : null}
            {!treeLoading && treeError ? <div className="tpEmpty">Tags could not be loaded. Close and try again.</div> : null}
            {!treeLoading && !treeError && topFolders.map((folder) => (
              <FolderNode
                key={folderID(folder)}
                folder={folder}
                foldersByParent={foldersByParent}
                depth={0}
                expanded={expanded}
                selectedPath={displayValue}
                onToggle={toggleFolder}
                onLoadMore={(id) => loadFolder(id, true)}
                onSelect={select}
              />
            ))}
            {!treeLoading && rootTags.map((tag, index) => (
              <TagRow key={tagKey(tag, index)} tag={tag} selected={tagPath(tag) === displayValue} onSelect={select} />
            ))}
            {!treeLoading && root?.tagsError ? (
              <button type="button" className="tpLoadMore" onClick={() => loadFolder(ROOT_FOLDER_ID, false)}>
                Unfiled tags could not be loaded. Try again.
              </button>
            ) : null}
            {!treeLoading && root?.tagsLoaded && rootTags.length < Number(root.tagCount || 0) ? (
              <button type="button" className="tpLoadMore" onClick={() => loadFolder(ROOT_FOLDER_ID, true)}>
                Load more unfiled tags · {rootTags.length.toLocaleString()} of {Number(root.tagCount).toLocaleString()}
              </button>
            ) : null}
            {!treeLoading && !treeError && !topFolders.length && Number(root?.tagCount || 0) === 0 ? (
              <div className="tpEmpty">No tags available.</div>
            ) : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div className="tpWrap" ref={wrapRef}>
      <div
        className={`gwInput tpInput${disabled ? " tpInputDisabled" : ""}`}
        ref={inputRef}
      >
        <button
          type="button"
          className="tpInputToggle"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span className={displayValue || value ? "tpInputValue" : "tpInputPlaceholder"}>
            {displayValue || (value ? "Selected tag" : placeholder)}
          </span>
        </button>
        {value || displayValue ? (
          <button
            type="button"
            className="tpClear"
            disabled={disabled}
            onClick={() => onChange("", "")}
            title="Clear selection"
            aria-label="Clear selected tag"
          >
            ×
          </button>
        ) : null}
        <span className="gwSelect-caret" />
      </div>
      {menu}
    </div>
  );
}
